import { createBrowserRouter, Navigate } from 'react-router-dom';

import { AppLayout } from '@/app/AppLayout';
import { LegacyFormRedirect } from '@/app/LegacyFormRedirect';
import { RequireAuth } from '@/app/guards/RequireAuth';
import { HomePage } from '@/features/home';
import { LoginPage } from '@/features/auth';
import { CalendarPage } from '@/features/calendar';
import { CoordinationDetailPage, CoordinationsPage, MinistryDetailPage } from '@/features/org';
import {
  DraftsPage,
  ProjectDetailPage,
  ProjectFormPage,
  ProjectsPage,
} from '@/features/projects';
import { ReviewQueuePage } from '@/features/review';

/**
 * `/login` é a única rota pública, fora de `RequireAuth`; tudo o mais entra
 * sob o guard + `AppLayout` (architecture.md §5.3). Login é por código de 6
 * dígitos digitado na própria tela — não há rota de callback de link.
 * Novas rotas autenticadas viram filhas do mesmo grupo — não duplique o guard.
 *
 * **URL em português** (é interface); código em inglês (code-conventions.md).
 *
 * Três famílias de rota, e a diferença importa:
 *
 * - `/coordenacoes` e `/projetos` são **pontos de partida**, e por isso são os
 *   itens da sidebar.
 * - `/coordenacoes/:id`, `/ministerios/:id` e `/projetos/:id` são
 *   **permalinks de entidade**: só fazem sentido depois de você ter escolhido
 *   alguma coisa. O endereço de um deles não carrega por onde a pessoa passou
 *   para chegar nele, senão o mesmo ministério teria dois endereços conforme
 *   se veio da estrutura ou de um projeto. Quem mostra a hierarquia é a trilha
 *   de navegação, e ela é a mesma pelos dois caminhos.
 *
 *   ⚠️ Que `/coordenacoes/:id` fique DENTRO do prefixo da lista é coincidência
 *   de nome, não hierarquia de caminho: lista e permalink falam da mesma
 *   entidade, então o prefixo é o dela. `/ministerios/:id` continua fora de
 *   qualquer prefixo pela razão original — não existe `/coordenacoes/:id/
 *   ministerios/:id`, e não deve existir.
 * - `/calendario/novo` e `/calendario/:id/editar` são **rotas de trabalho**: o
 *   formulário aberto a partir da grade. Elas moram sob `/calendario` porque é
 *   de lá que se chega a elas — ver o docblock de `LegacyFormRedirect`.
 *
 * Nenhuma destas rotas tem guard de papel, e isso é deliberado: as três telas
 * de conteúdo existem para todos os papéis, com conteúdo recortado pela RLS.
 *
 * `/analise` também não tem guard — nem ela, a fila de quem decide (ADR-012):
 * um líder que digitar a URL vê fila vazia, não erro e não os projetos dos
 * outros. Quem decide isso é a própria página (`ReviewQueuePage`, que não
 * dispara a consulta sem `reviewProject`), não uma rota protegida. Guard de
 * rota aqui seria conveniência de UX a mais para manter em sincronia com a
 * RPC, nunca a defesa — essa já é `can_decide_for_ministry()` no banco.
 */
export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: '/', element: <HomePage /> },

          { path: '/coordenacoes', element: <CoordinationsPage /> },
          // O endereço que esta tela TEVE, de volta ao papel de legado: ela
          // nasceu `/organograma`, e o nome prometia um desenho de hierarquia
          // que a tela nunca desenhou. Link já compartilhado continua abrindo.
          { path: '/organograma', element: <Navigate to="/coordenacoes" replace /> },
          { path: '/coordenacoes/:id', element: <CoordinationDetailPage /> },
          { path: '/ministerios/:id', element: <MinistryDetailPage /> },

          { path: '/projetos', element: <ProjectsPage /> },
          { path: '/projetos/:id', element: <ProjectDetailPage /> },
          // Os dois endereços que o formulário TINHA. Continuam abrindo, e
          // continuam sendo os únicos que alguém pode ter guardado.
          { path: '/projetos/novo', element: <LegacyFormRedirect /> },
          { path: '/projetos/:id/editar', element: <LegacyFormRedirect /> },
          { path: '/rascunhos', element: <DraftsPage /> },
          // O Calendário é um LUGAR, não uma fila: sem guard de papel, como as
          // demais telas de conteúdo. O que cada pessoa enxerga ali é decidido
          // pela RLS e pela RPC `agenda_projects` (spec-calendar.md §2, §3.3).
          //
          // ⚠️ **`/calendario/:id` não existe, e a ausência é a decisão** (1ª
          // rodada da análise, decisão 1): duas telas do mesmo projeto divergem
          // em meses. Clicar num item do calendário leva a `/projetos/:id`, com
          // a origem viajando em `location.state`.
          { path: '/calendario', element: <CalendarPage /> },
          // `/novo` vem ANTES de qualquer rota de um segmento com parâmetro,
          // pela mesma razão de sempre: "novo" não pode casar com um `:id`.
          // As duas apontam para o MESMO componente — criar e editar são a
          // mesma tela, e é isso que faz o rascunho continuar de onde parou
          // (US-17).
          { path: '/calendario/novo', element: <ProjectFormPage /> },
          // A MESMA tela de `/rascunhos`, com endereço próprio dentro do
          // Calendário — e a duplicação de rota é a decisão, não um descuido.
          //
          // O botão "Rascunhos" da barra da grade mandava para `/rascunhos`,
          // que é gaveta de "Projetos" (`owns`). Para `admin`, `overseer` e
          // `coordinator` — os que TÊM "Projetos" no menu — o clique apagava o
          // Calendário da sidebar e trocava a aba no meio da tarefa: a pessoa
          // pediu para ver o que ela largou pela metade e o painel respondeu
          // mudando de assunto. O `fallbackFor` do Calendário resolvia isso só
          // para quem não tem "Projetos" (decisão 97), ou seja, exatamente para
          // quem não reclamava.
          //
          // Rota nova em vez de mais um `fallbackFor`: adoção é condicional à
          // visibilidade, e aqui a resposta não pode depender de papel — quem
          // entra pelo Calendário fica no Calendário, os cinco papéis. O
          // endereço antigo continua existindo, servido pela mesma página, e é
          // ele que "Projetos" continua possuindo.
          { path: '/calendario/rascunhos', element: <DraftsPage /> },
          { path: '/calendario/:id/editar', element: <ProjectFormPage /> },
          { path: '/analise', element: <ReviewQueuePage /> },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
